import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ITrajectoryDetail,
  TrajectorySectionCode,
  TrajectoryViewMode,
} from 'src/config/api/models/Trajectory';
import TrajectorySectionMenu from './TrajectorySectionMenu';
import { StyledContent, StyledSubHeader } from '../Atoms';
import { Tooltip, useMediaQuery, useTheme } from '@material-ui/core';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { useUser } from 'src/modules/shared/hooks';
import { FontAwesomeSpriteButton } from '../../FontAwesomeLocal';
import { SpriteIconsLight, SpriteIconsSolid } from 'src/config/api/models/FontAwesome';
import { useI18nPath } from 'src/config/clientRoutes/hooks';
import { useHistory, useParams } from 'react-router';
import { TrajectoryTeacherFilters } from '../../TeacherFilters/TrajectoryTeacherFilters';
import { setActiveFilter } from 'src/modules/shared/slices/filters/entity/FiltersSlice';
import { showHelpModals, TeacherFiltersEnum } from 'src/modules/shared/constants';
import AugmentationButton from '../../AugmentationButton';
import { BlockedFilters } from '../../TeacherFilters/BookPreviewFiltersContent';
import { MenuFilterWrapper, StyledAuthorDiv } from './Atoms';
import { UrlParams } from 'src/modules/shared/types';
import {
  changePanelContent,
  openSidePanel,
} from 'src/modules/shared/slices/sidePanel/sidePanelSlice';
import { isSchoolHours } from 'src/modules/shared/helpers/isSchoolHours';
import DemoDialog, { ModalProps } from 'src/modules/app/components/Demo/DemoDialog';
import { LogActionTypes } from 'src/modules/shared/data-logging';
import { setHasSeenFollowUp } from 'src/modules/shared/slices/ctas/CtasSlice';
import SpeechBubbleTooltip from 'src/components/SpeechBubbleTooltip/SpeechBubbleTooltip';
import { progressAndScoreTooltipContent } from './TrajectorySubheaderTooltip';

type TrajectorySubHeaderProps = {
  trajectory: ITrajectoryDetail;
  bookId: string | undefined;
  onSelectedSectionChange: (newSectionCode: TrajectorySectionCode) => void;
  mode?: TrajectoryViewMode;
  pageId?: string;
  defaultSection?: TrajectorySectionCode;
} & React.HTMLAttributes<HTMLDivElement>;

const env = process.env.REACT_APP_ENVIRONMENT;

const TrajectorySubHeader: FC<TrajectorySubHeaderProps> = ({
  trajectory,
  bookId,
  onSelectedSectionChange,
  pageId,
  defaultSection,
  ...restProps
}) => {
  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const { isTeacher, isAuthor, isSuperAdmin } = useUser();
  const { getPath } = useI18nPath();
  const history = useHistory();
  const { chapterId } = useParams<UrlParams>();
  const [showDialog, setShowDialog] = useState<boolean>(false);

  // redux
  const dispatch = useAppDispatch();
  const book = useAppSelector((state) => state.privatePageSettings.book);
  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const sidepanel = useAppSelector((state) => state.sidepanel.sidepanel);
  const hasSeenFollowUp = useAppSelector((state) => state.ctasSlice.hasSeenFollowUp);
  const showPresentationMode = useAppSelector(
    (state) => state.presentationMode.showPresentationMode
  );

  const followUpModalProps: ModalProps = {
    title: 'Volg leerlingen eenvoudig op',
    hasSeen: () => dispatch(setHasSeenFollowUp(true)),
    body: [
      'Bekijk leerlingen hun progressie, hun score op elke poging én hun antwoorden.',
      'In deze demo-omgeving hebben de fictieve leerlingen geen scores en antwoorden.',
    ],
    videoUrl: 'https://vimeo.com/912575260?share=copy',
    checkboxLabel: 'Verberg deze melding.',
    buttonText: 'SLUIT',
    log: LogActionTypes.FOLLOW_UP_SEEN_CTA,
  };

  const previewModeFiltersContent = useMemo(() => {
    return BlockedFilters();
  }, []);

  const handleReturnToBookPage = useCallback(() => {
    if (bookId) {
      history.push(
        `${getPath(isAuthor ? 'book' : 'bookOverview', { bookId })}${
          !isAuthor && chapterId ? `/hoofdstukken/${chapterId}` : ''
        }`,
        { id: chapterId }
      );
    }
    dispatch(openSidePanel(false));
    dispatch(changePanelContent(undefined));
  }, [history, bookId, getPath, isAuthor, chapterId, dispatch]);

  useEffect(() => {
    if (isTeacher) {
      if (selectedGroup !== '') dispatch(setActiveFilter(TeacherFiltersEnum.FOLLOW_UP));
      else {
        dispatch(setActiveFilter(undefined));
      }
    }
  }, [isTeacher, dispatch, selectedGroup]);

  const handleSwitchTeacherFilterOn = useCallback(
    (teacherFilter: TeacherFiltersEnum) => {
      if (teacherFilter !== activeFilter) {
        dispatch(setActiveFilter(teacherFilter));
      }
    },
    [activeFilter, dispatch]
  );

  const handleModal = useCallback(() => {
    setShowDialog(
      !hasSeenFollowUp &&
        (env === 'demo' || (env !== 'demo' && !isSchoolHours() && isTeacher)) &&
        showHelpModals
    );
  }, [hasSeenFollowUp, isTeacher]);

  const button = document.getElementById('user-menu-button');

  const handleAugmentationButtonClick = useCallback(() => {
    if (env !== 'demo') {
      handleSwitchTeacherFilterOn(TeacherFiltersEnum.FOLLOW_UP);
      handleModal();
    }
  }, [handleModal, handleSwitchTeacherFilterOn]);

  return (
    <StyledSubHeader
      sidepanelwidth={sidepanel?.width}
      presentationmode={showPresentationMode}
      {...restProps}
    >
      <StyledContent width={sidepanel?.width}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <FontAwesomeSpriteButton
            iconSize="lg"
            color={theme.palette.secondary.main}
            icon={SpriteIconsLight.BOOK}
            onClick={handleReturnToBookPage}
          />
          <TrajectorySectionMenu
            trajectory={trajectory}
            pageId={pageId}
            onSelectedSectionChange={onSelectedSectionChange}
            defaultSection={defaultSection}
          />
        </div>
        {!isMobile && isTeacher && !showPresentationMode && (
          <MenuFilterWrapper>
            <SpeechBubbleTooltip title={progressAndScoreTooltipContent} placement="left-start">
              <AugmentationButton
                disabled={env === 'demo'}
                icon={SpriteIconsLight.PROGRESS_AND_SCORES}
                //tooltip={book?.isPreview ? '' : 'Opvolging'}
                blocked={book?.isPreview}
                blockedMessage={previewModeFiltersContent.find((m) => m.id === 'scores')!.message}
                onButtonClick={() => handleAugmentationButtonClick()}
                primary={activeFilter === TeacherFiltersEnum.FOLLOW_UP}
              />
            </SpeechBubbleTooltip>
            <TrajectoryTeacherFilters currentBookId={bookId} />
          </MenuFilterWrapper>
        )}
        {(isAuthor || isSuperAdmin) && book !== undefined && (
          <StyledAuthorDiv style={{ width: button?.offsetWidth }}>
            <Tooltip title={'Naar trajectenlijst'}>
              <span>
                <FontAwesomeSpriteButton
                  buttonStyle={{
                    backgroundColor: '#ffffff',
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    marginRight: '16px',
                    cursor: 'pointer',
                    color: 'rgba(0,0,0,0.56)',
                  }}
                  icon={SpriteIconsSolid.ARROW_UP}
                  iconSize="md"
                  color={'rgba(0,0,0,0.56)'}
                  onClick={() => history.push(`/nl-be/boeken/${bookId}/hoofdstukken/${chapterId}`)}
                />
              </span>
            </Tooltip>

            <h4 style={{ margin: '0' }}>{book.title}</h4>
          </StyledAuthorDiv>
        )}
      </StyledContent>
      {isTeacher && (
        <DemoDialog
          showDialog={showDialog}
          onClose={() => setShowDialog(false)}
          props={followUpModalProps}
        />
      )}
    </StyledSubHeader>
  );
};

export default TrajectorySubHeader;
